import { Pencil, MessageSquareWarning, Ban, Undo2, Trash2 } from "lucide-react";
import { ParcelQueryBadge, openParcelQuery } from "./ParcelQueryDialog";
import { WorkingTag, workLook } from "./WorkingParcel";
import { ParcelNoteBadge, HL } from "./ParcelNotes";
import { OutsourcedTag } from "./OutsourcedTag";

const fmtDmy = (v) => { const s = String(v ?? "").trim(); const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/); return m ? `${m[3]}/${m[2]}/${m[1]}` : s; };

const statusChip = (r, invOwners) => {
  if (r.cancelled) return ["CANCELLED", "bg-pink-200 text-pink-900"];
  if (!r.invoice_no) return [(r.reason || "").trim() ? "PENDING · REASON" : "NO INVOICE", "bg-rose-100 text-rose-700"];
  // Row status comes from the list API (same as the PC table); invOwners only holds running invoices.
  const st = r.invoice_status || invOwners[(r.invoice_no || "").toUpperCase()]?.status;
  return st === "completed" ? ["COMPLETED", "bg-emerald-100 text-emerald-700"] : ["IN PROGRESS", "bg-amber-100 text-amber-800"];
};

// Phone view of the Order Register: one card per parcel; tap any line to open the full edit view.
export const OrMobileCards = ({ rows, parcelKey, invOwners, onOpen, workMap = {}, noteProps = {}, actions = null }) => {
  const groups = [];
  const ix = {};
  for (const r of rows) {
    const k = parcelKey(r);
    if (!(k in ix)) { ix[k] = groups.length; groups.push([]); }
    groups[ix[k]].push(r);
  }
  return (
    <div className="md:hidden space-y-2" data-testid="or-mobile-cards">
      {groups.map((g, i) => {
        const r = g[0];
        const [st, tone] = statusChip(r, invOwners || {});
        const w = g.map(x => workMap[x.id]).find(Boolean);
        const act = actions && !r.view_only ? actions : null;
        return (
          <div key={i} className={`rounded-xl border px-3 py-2.5 ${w ? "font-semibold border-l-4" : r.cancelled ? "border-pink-300 bg-pink-50/60" : "bg-white border-slate-200"}`}
            style={w ? { backgroundColor: workLook(w).bg, borderColor: workLook(w).bar } : undefined}
            data-card-working={w ? w.email : undefined} data-testid={`or-card-${i}`}>
            {w && <div className="mb-1"><WorkingTag w={w} testid={`or-card-working-${i}`} /></div>}
            <button type="button" className="w-full text-left" onClick={() => onOpen(r)} data-testid={`or-card-open-${i}`}>
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900 break-words">{r.customer_name || "—"}</div>
                  <div className="text-[11px] text-slate-500 break-all">{r.code} · {r.date}</div>
                </div>
                <div className="flex flex-wrap justify-end gap-1 shrink-0 max-w-[45%]">
                  {r.mode && <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-semibold">{r.mode}</span>}
                  <OutsourcedTag mode={r.mode} testid={`or-card-outsourced-${i}`} />
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${tone}`}>{st}</span>
                </div>
              </div>
              <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
                <div className="min-w-0"><span className="text-slate-400">Tracking </span><span className="font-mono font-medium text-slate-700 break-all">{r.tracking || "—"}</span></div>
                <div className="min-w-0"><span className="text-slate-400">Invoice </span><span className="font-semibold text-slate-800">{r.invoice_no || "—"}</span></div>
                <div className="min-w-0"><span className="text-slate-400">Country </span><span className="font-medium text-slate-700">{r.country_display || "—"}</span></div>
                <div className="min-w-0" data-testid={`or-card-invdate-${i}`}><span className="text-slate-400">Inv. date </span><span className="font-medium text-slate-700">{fmtDmy(r.invoice_date) || "—"}</span></div>
                {r.reason && <div className="min-w-0 col-span-2 text-rose-700 truncate">Reason: {r.reason}</div>}
                {r.notes && <div className="min-w-0 col-span-2 text-slate-600 break-words" data-testid={`or-card-notes-${i}`}><span className="text-slate-400">Notes </span>{r.notes}</div>}
              </div>
            </button>
            {r.pnote?.last && (
              <div className="mt-1 rounded px-1.5 py-1 text-[11px] text-slate-700 bg-yellow-50 break-words" data-testid={`or-card-teamnote-${i}`}
                style={r.pnote.last.hl ? { backgroundColor: HL[r.pnote.last.hl]?.bg } : undefined}>
                <b>{r.pnote.last.by_name}:</b> {r.pnote.last.text}{r.pnote.count > 1 ? <span className="text-slate-400"> (+{r.pnote.count - 1} more)</span> : null}
              </div>)}
            <div className="mt-1 flex items-center gap-2" data-testid={`or-card-query-${i}`}>
              <span className="inline-flex items-center gap-1 text-[11px] text-slate-500" data-testid={`or-card-note-${i}`}>
                <ParcelNoteBadge row={r} rows={g} {...noteProps} />{r.pnote?.count ? "Team notes" : noteProps.readOnly ? null : "Add note"}</span>
              {r.pquery ? <ParcelQueryBadge q={r.pquery} rowId={r.id} /> : null}
              <button type="button" onClick={() => openParcelQuery(r.id)} data-testid={`or-card-query-btn-${i}`}
                className="ml-auto inline-flex items-center gap-1 rounded-full border border-orange-300 bg-orange-50 px-2.5 py-1 text-[11px] font-semibold text-orange-800 active:bg-orange-100">
                <MessageSquareWarning className="w-3.5 h-3.5" /> {r.pquery ? "Query thread" : "Query"}
              </button>
            </div>
            <div className="mt-1.5 border-t border-slate-100 pt-1 space-y-0.5">
              {g.map((x, j) => (
                <div key={x.id} className="flex items-center gap-1">
                  <button type="button" onClick={() => onOpen(x)} data-testid={`or-card-item-${i}-${j}`}
                    className="flex-1 min-w-0 flex items-center gap-2 text-left text-[11px] py-0.5 active:bg-indigo-50 rounded">
                    <span className="flex-1 min-w-0 truncate text-slate-700">{x.medicine}{x.second_name ? <span className="text-slate-400"> · {x.second_name}</span> : null}</span>
                    <span className="font-bold text-slate-900">×{x.quantity}</span>
                    <Pencil className="w-3 h-3 text-indigo-500" />
                  </button>
                  {actions && !x.view_only && <button type="button" onClick={() => actions.remove(x)} title="Delete this product line" aria-label="Delete product"
                    className="p-1.5 rounded text-slate-400 active:bg-rose-50 active:text-rose-600" data-testid={`or-card-del-${i}-${j}`}>
                    <Trash2 className="w-3.5 h-3.5" /></button>}
                </div>
              ))}
            </div>
            {act && (
              <div className="mt-1.5 flex justify-end border-t border-slate-100 pt-1.5">
                {r.cancelled ? (
                  <button type="button" onClick={() => act.restore(r)} data-testid={`or-card-restore-${i}`}
                    className="inline-flex items-center gap-1 rounded-full border border-emerald-400 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-800 active:bg-emerald-100">
                    <Undo2 className="w-3.5 h-3.5" /> Undo cancel</button>
                ) : (
                  <button type="button" onClick={() => act.cancel(r)} data-testid={`or-card-cancel-${i}`}
                    className="inline-flex items-center gap-1 rounded-full border border-rose-300 bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-700 active:bg-rose-100">
                    <Ban className="w-3.5 h-3.5" /> Cancel parcel</button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
